import { useEffect, useRef, useState } from "react";
import type { Language } from "../i18n";
import { t } from "../i18n";

export interface ZoomControlsProps {
  zoom: number;
  minZoom?: number;
  maxZoom?: number;
  stepZoom?: number;
  isBatchMode?: boolean;
  onZoomChange: (zoom: number) => void;
  onResetZoom: (preset?: number) => void;
  language?: Language;
}

export function ZoomControls({
  zoom,
  minZoom = 1,
  maxZoom = 5,
  stepZoom = 0.1,
  isBatchMode = false,
  onZoomChange,
  onResetZoom,
  language = "ru",
}: ZoomControlsProps) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!expanded) return;

    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(event.target as Node)) {
        setExpanded(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [expanded]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (!Number.isNaN(val)) {
      onZoomChange(val);
    }
  };

  const is1xActive = Math.abs(zoom - 1) < 0.05;
  const is2xActive = Math.abs(zoom - 2) < 0.05;
  const zoomLabel = `${zoom.toFixed(1)}x`;
  const modeClass = isBatchMode ? "zoom-controls--batch" : "";

  if (!expanded) {
    return (
      <div className={`zoom-controls zoom-controls--collapsed ${modeClass}`} data-testid="zoom-controls" ref={rootRef}>
        <button
          type="button"
          className="zoom-toggle-btn"
          onClick={() => setExpanded(true)}
          aria-label={t(language, "zoom_toggle_expand_aria", { zoom: zoomLabel })}
          aria-expanded={false}
          data-testid="zoom-toggle"
        >
          {zoomLabel}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`zoom-controls zoom-controls--expanded ${modeClass}`}
      data-testid="zoom-controls"
      ref={rootRef}
    >
      <button
        type="button"
        className="zoom-toggle-btn zoom-toggle-btn--open"
        onClick={() => setExpanded(false)}
        aria-label={t(language, "zoom_toggle_collapse_aria")}
        aria-expanded={true}
        data-testid="zoom-toggle"
      >
        {zoomLabel}
      </button>

      <div className="zoom-controls__presets">
        <button
          type="button"
          className={`zoom-preset-btn ${is1xActive ? "zoom-preset-btn--active" : ""}`}
          onClick={() => onResetZoom(1)}
          aria-label={t(language, "zoom_reset_1x_aria")}
          data-testid="zoom-preset-1x"
        >
          1x
        </button>
        {maxZoom >= 2 && (
          <button
            type="button"
            className={`zoom-preset-btn ${is2xActive ? "zoom-preset-btn--active" : ""}`}
            onClick={() => onResetZoom(2)}
            aria-label={t(language, "zoom_set_2x_aria")}
            data-testid="zoom-preset-2x"
          >
            2x
          </button>
        )}
      </div>

      <div className="zoom-controls__slider-wrapper">
        <input
          type="range"
          className="zoom-slider"
          min={minZoom}
          max={maxZoom}
          step={stepZoom}
          value={zoom}
          onChange={handleSliderChange}
          aria-label={t(language, "zoom_slider_aria")}
          data-testid="zoom-slider"
        />
      </div>
    </div>
  );
}
